/**
 * GovernanceUsersPage — Usuários & Acessos da Governança VUKZ
 * Rota: /governance/usuarios
 *
 * Abas:
 *   1. Usuários     — lista por tenant, criar, editar, desativar
 *   2. Papéis       — roles agrupados por produto
 *   3. Permissões   — matriz módulo × ação
 *   4. Planos       — entitlements com limites e alertas de upgrade
 *   5. Logs         — audit trail filtrado
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AZURE } from '../../services/api';
import {
  Users, Shield, Grid, CreditCard, FileText,
  Plus, Search, RefreshCw, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle, XCircle, Edit2, Trash2,
  X, Loader
} from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

// ─── Design system ────────────────────────────────────────────────────────
const G = '#064e3b';
const GOLD = '#b48c2c';
const BG = '#f8fafc';

// ─── i18n dictionary ────────────────────────────────────────────────────────
const getT = (lang) => lang === 'en' ? {
  pageTitle: 'Users & Access',
  pageSubtitle: 'Manage users, roles, permissions and plans for the VUKZ platform.',
  tabUsers: 'Users', tabRoles: 'Roles', tabPermissions: 'Permissions', tabPlans: 'Plans', tabLogs: 'Logs',
  statusActive: 'Active', statusInactive: 'Inactive', statusInvited: 'Invited',
  editBtn: 'Edit', deactivateBtn: 'Deactivate', deactivatingBtn: '...',
  editUserTitle: 'Edit user', newUserTitle: 'New user',
  tenantLabel: 'Tenant (institution_id) *', tenantPlaceholder: 'Institution ID',
  emailLabel: 'E-mail *', nameLabel: 'Name', titleLabel: 'Job title', areaLabel: 'Department',
  statusLabel: 'Status', rolesLabel: 'Roles',
  cancelBtn: 'Cancel', saveBtn: 'Save', savingBtn: 'Saving...', createUserBtn: 'Create user',
  saveErrorDefault: 'Failed to save user.',
  searchPlaceholder: 'Search by name or e-mail...',
  allProducts: 'All products', allStatuses: 'All statuses',
  activeFilter: 'Active', inactiveFilter: 'Inactive', invitedFilter: 'Invited',
  newUserBtn: 'New user',
  sectionGovernance: 'Governance', sectionSports: 'Sports',
  sectionVukzInternal: 'VUKZ Internal', sectionNoProduct: 'No product',
  emptyUsers: 'No users found. Create the first one.',
  deactivateConfirm: 'Deactivate this user?',
  permissionsSubtitle: 'Inclusion matrix by product × role. Roles marked ✓ are included in the plan; add-ons are optional.',
  colModule: 'Module / Product',
  legendMandatory: 'Mandatory', legendIncluded: 'Included', legendAddOn: 'Add-on (sold separately)',
  emptySemData: 'No permission data.',
  plansSubtitle: 'Each plan requires mandatory managers to be activated. Additional users above the included limit incur per-user charges.',
  perMonth: '/ mo', mandatoryToActivate: 'Required to activate',
  hideDetails: 'Hide details', showDetails: 'Show included users',
  includedInPlanLabel: 'Included in plan',
  availableLabel: 'included', unlimitedLabel: 'unlimited', maxLabel: 'max', extraLabel: '/extra',
  addOnsAvailableLabel: 'Available add-ons', perUserLabel: '/user',
  commercialRulesTitle: 'Commercial rules',
  rule1: 'Governance plan requires at least 1 active Governance Manager.',
  rule2: 'Sports plan requires at least 1 active Sports Manager.',
  rule3: 'Full Suite plan requires 1 Governance Manager + 1 Sports Manager active.',
  rule4: 'A single user can hold both manager roles in the Full Suite.',
  rule5: 'Doctor and Physiotherapist are not part of the current offering.',
  logEmailPlaceholder: 'User e-mail',
  logModulePlaceholder: 'Module (users, finance...)',
  logActionPlaceholder: 'Action (create, update...)',
  filterBtn: 'Filter',
  recordsFound: (n) => `${n} record${n !== 1 ? 's' : ''} found`,
  emptyLogs: 'No logs found for the selected filters.',
  prevPage: '← Previous', nextPage: 'Next →',
  emptyDefault: 'No results found.',
  dateLocale: 'en-US',
} : {
  pageTitle: 'Usuários & Acessos',
  pageSubtitle: 'Gerencie usuários, papéis, permissões e planos da plataforma VUKZ.',
  tabUsers: 'Usuários', tabRoles: 'Papéis', tabPermissions: 'Permissões', tabPlans: 'Planos', tabLogs: 'Logs',
  statusActive: 'Ativo', statusInactive: 'Inativo', statusInvited: 'Convidado',
  editBtn: 'Editar', deactivateBtn: 'Desativar', deactivatingBtn: '...',
  editUserTitle: 'Editar usuário', newUserTitle: 'Novo usuário',
  tenantLabel: 'Tenant (institution_id) *', tenantPlaceholder: 'ID da instituição',
  emailLabel: 'E-mail *', nameLabel: 'Nome', titleLabel: 'Cargo', areaLabel: 'Área',
  statusLabel: 'Status', rolesLabel: 'Papéis',
  cancelBtn: 'Cancelar', saveBtn: 'Salvar', savingBtn: 'Salvando...', createUserBtn: 'Criar usuário',
  saveErrorDefault: 'Erro ao salvar usuário.',
  searchPlaceholder: 'Buscar por nome ou e-mail...',
  allProducts: 'Todos os produtos', allStatuses: 'Todos os status',
  activeFilter: 'Ativos', inactiveFilter: 'Inativos', invitedFilter: 'Convidados',
  newUserBtn: 'Novo usuário',
  sectionGovernance: 'Governança', sectionSports: 'Sports',
  sectionVukzInternal: 'VUKZ Interno', sectionNoProduct: 'Sem produto',
  emptyUsers: 'Nenhum usuário encontrado. Crie o primeiro.',
  deactivateConfirm: 'Desativar este usuário?',
  permissionsSubtitle: 'Matriz de inclusão por produto × papel. Papéis marcados com ✓ estão inclusos no plano; add-ons são opcionais.',
  colModule: 'Módulo / Produto',
  legendMandatory: 'Obrigatório', legendIncluded: 'Incluso', legendAddOn: 'Add-on (pago separado)',
  emptySemData: 'Sem dados de permissões.',
  plansSubtitle: 'Cada plano exige os gestores obrigatórios para ser ativado. Usuários adicionais acima do limite incluem cobrança por usuário.',
  perMonth: '/ mês', mandatoryToActivate: 'Obrigatório para ativar',
  hideDetails: 'Ocultar detalhes', showDetails: 'Ver usuários inclusos',
  includedInPlanLabel: 'Inclusos no plano',
  availableLabel: 'incluso', unlimitedLabel: 'ilimitado', maxLabel: 'máx', extraLabel: '/extra',
  addOnsAvailableLabel: 'Add-ons disponíveis', perUserLabel: '/usuário',
  commercialRulesTitle: 'Regras comerciais',
  rule1: 'Plano Governance exige pelo menos 1 Gestor Governança ativo.',
  rule2: 'Plano Sports exige pelo menos 1 Gestor Sports ativo.',
  rule3: 'Plano Full Suite exige 1 Gestor Governança + 1 Gestor Sports ativos.',
  rule4: 'Um mesmo usuário pode acumular os dois papéis de gestor no Full Suite.',
  rule5: 'Médico e Fisioterapeuta não fazem parte da oferta atual.',
  logEmailPlaceholder: 'E-mail do usuário',
  logModulePlaceholder: 'Módulo (users, financeiro...)',
  logActionPlaceholder: 'Ação (create, update...)',
  filterBtn: 'Filtrar',
  recordsFound: (n) => `${n} registro${n !== 1 ? 's' : ''} encontrado${n !== 1 ? 's' : ''}`,
  emptyLogs: 'Nenhum log encontrado para os filtros selecionados.',
  prevPage: '← Anterior', nextPage: 'Próxima →',
  emptyDefault: 'Nenhum resultado encontrado.',
  dateLocale: 'pt-BR',
};

// ─── Utilities ────────────────────────────────────────────────────────────
const PRODUCT_LABEL = {
  GOVERNANCE: 'Governance',
  SPORTS:     'Sports',
  FULL_SUITE: 'Full Suite',
  BOTH:       'VUKZ Interno',
};
const PRODUCT_COLOR = {
  GOVERNANCE: { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' },
  SPORTS:     { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
  FULL_SUITE: { bg: '#faf5ff', text: '#6b21a8', border: '#e9d5ff' },
  BOTH:       { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
};
const STATUS_COLOR = {
  active:   { bg: '#dcfce7', text: '#166534' },
  inactive: { bg: '#fee2e2', text: '#991b1b' },
  invited:  { bg: '#fef9c3', text: '#854d0e' },
};

function Badge({ label, scheme }) {
  const c = scheme || { bg: '#f1f5f9', text: '#475569' };
  return (
    <span style={{
      backgroundColor: c.bg, color: c.text,
      border: `1px solid ${c.border || c.bg}`,
      borderRadius: 6, padding: '2px 8px',
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {label}
    </span>
  );
}

function TabButton({ label, icon: Icon, active, onClick, count }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '10px 16px',
      borderBottom: active ? `2px solid ${G}` : '2px solid transparent',
      color: active ? G : '#6b7280',
      fontWeight: active ? 700 : 500,
      fontSize: 14,
      background: 'none', border: 'none',
      borderBottom: active ? `2px solid ${G}` : '2px solid transparent',
      cursor: 'pointer', whiteSpace: 'nowrap',
      transition: 'color .15s',
    }}>
      <Icon size={15} />
      {label}
      {count != null && (
        <span style={{
          backgroundColor: active ? G : '#e5e7eb',
          color: active ? '#fff' : '#6b7280',
          borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 700
        }}>{count}</span>
      )}
    </button>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af' }}>
      <FileText size={40} style={{ margin: '0 auto 12px', opacity: .4 }} />
      <p style={{ fontSize: 14 }}>{message}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <Loader size={28} style={{ color: G, animation: 'spin 1s linear infinite' }} />
    </div>
  );
}

// ─── Modal de criação/edição ──────────────────────────────────────────────
function UserModal({ onClose, onSaved, editUser, roles, t }) {
  const isEdit = !!editUser;
  const [form, setForm] = useState({
    institution_id: editUser?.institution_id ?? '',
    email:  editUser?.email  ?? '',
    name:   editUser?.name   ?? '',
    title:  editUser?.title  ?? '',
    area:   editUser?.area   ?? '',
    status: editUser?.status ?? 'active',
    roles:  (editUser?.roles || []).map(r => r.role_key),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const toggle = (key) =>
    setForm(f => ({
      ...f,
      roles: f.roles.includes(key)
        ? f.roles.filter(r => r !== key)
        : [...f.roles, key],
    }));

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      if (isEdit) {
        await AZURE.patch(`/governance/users/${editUser.id}`, {
          name: form.name, title: form.title,
          area: form.area, status: form.status,
          roles: form.roles,
        });
      } else {
        await AZURE.post('/governance/users', form);
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || t.saveErrorDefault);
    } finally {
      setSaving(false);
    }
  };

  const grouped = {};
  (roles || []).forEach(r => {
    const k = r.product_scope || 'OUTROS';
    (grouped[k] = grouped[k] || []).push(r);
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: G }}>
            {isEdit ? t.editUserTitle : t.newUserTitle}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {!isEdit && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              {t.tenantLabel}
            </label>
            <input
              type="number"
              value={form.institution_id}
              onChange={e => setForm(f => ({ ...f, institution_id: e.target.value }))}
              placeholder={t.tenantPlaceholder}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
            />
          </div>
        )}

        {[
          { key: 'email',  label: t.emailLabel,  type: 'email',  disabled: isEdit },
          { key: 'name',   label: t.nameLabel,   type: 'text' },
          { key: 'title',  label: t.titleLabel,  type: 'text' },
          { key: 'area',   label: t.areaLabel,   type: 'text' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              {f.label}
            </label>
            <input
              type={f.type}
              disabled={f.disabled}
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={{
                width: '100%', padding: '8px 12px',
                borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14,
                background: f.disabled ? '#f9fafb' : '#fff',
              }}
            />
          </div>
        ))}

        {isEdit && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{t.statusLabel}</label>
            <select
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
            >
              <option value="active">{t.statusActive}</option>
              <option value="inactive">{t.statusInactive}</option>
              <option value="invited">{t.statusInvited}</option>
            </select>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>
            {t.rolesLabel}
          </label>
          {Object.entries(grouped).map(([scope, scopeRoles]) => (
            <div key={scope} style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>
                {PRODUCT_LABEL[scope] || scope}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {scopeRoles.map(r => {
                  const selected = form.roles.includes(r.role_key);
                  return (
                    <button key={r.role_key} onClick={() => toggle(r.role_key)} style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', transition: 'all .15s',
                      background: selected ? G : '#f1f5f9',
                      color: selected ? '#fff' : '#374151',
                      border: selected ? `1px solid ${G}` : '1px solid #d1d5db',
                    }}>
                      {r.display_name || r.role_key}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            {t.cancelBtn}
          </button>
          <button onClick={save} disabled={saving} style={{
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: G, color: '#fff', fontWeight: 700, fontSize: 14,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? .7 : 1
          }}>
            {saving ? t.savingBtn : isEdit ? t.saveBtn : t.createUserBtn}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ABA 1: Usuários ──────────────────────────────────────────────────────
function TabUsuarios({ roles, t }) {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState({ product_scope: '', status: '' });
  const [modal, setModal]       = useState(null); // null | 'new' | user-object
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.product_scope) params.product_scope = filter.product_scope;
      if (filter.status)        params.status        = filter.status;
      if (search)               params.search        = search;
      const { data } = await AZURE.get('/governance/users', { params });
      setUsers(data?.data?.users || []);
    } catch (e) {
      console.error('[gov-users] load error', e);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const softDelete = async (id) => {
    if (!window.confirm(t.deactivateConfirm)) return;
    setDeleting(id);
    try {
      await AZURE.delete(`/governance/users/${id}`);
      load();
    } finally {
      setDeleting(null);
    }
  };

  const govUsers    = users.filter(u => u.roles?.some(r => r.product_scope === 'GOVERNANCE'));
  const sportsUsers = users.filter(u => u.roles?.some(r => r.product_scope === 'SPORTS'));
  const vukzUsers   = users.filter(u => u.roles?.some(r => r.product_scope === 'BOTH'));
  const otherUsers  = users.filter(u =>
    !u.roles?.length ||
    !u.roles.some(r => ['GOVERNANCE','SPORTS','BOTH'].includes(r.product_scope))
  );

  const UserCard = ({ user }) => (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
      padding: '14px 16px', display: 'flex',
      alignItems: 'center', gap: 14,
      transition: 'box-shadow .15s',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        background: G, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, fontWeight: 700, flexShrink: 0,
      }}>
        {(user.name || user.email || '?')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
            {user.name || '—'}
          </span>
          <Badge
            label={user.status === 'active' ? t.statusActive : user.status === 'invited' ? t.statusInvited : t.statusInactive}
            scheme={STATUS_COLOR[user.status]}
          />
        </div>
        <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0' }}>{user.email}</p>
        {user.title && <p style={{ fontSize: 12, color: '#9ca3af' }}>{user.title}{user.area ? ` · ${user.area}` : ''}</p>}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
          {(user.roles || []).map(r => (
            <Badge
              key={r.role_key}
              label={r.display_name || r.role_key}
              scheme={PRODUCT_COLOR[r.product_scope]}
            />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button onClick={() => setModal(user)} style={{
          padding: '6px 10px', borderRadius: 7, border: '1px solid #e5e7eb',
          background: '#f9fafb', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
        }}>
          <Edit2 size={13} /> {t.editBtn}
        </button>
        {user.status !== 'inactive' && (
          <button onClick={() => softDelete(user.id)} disabled={deleting === user.id} style={{
            padding: '6px 10px', borderRadius: 7, border: '1px solid #fecaca',
            background: '#fff5f5', color: '#dc2626', cursor: 'pointer', fontSize: 12,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <Trash2 size={13} /> {deleting === user.id ? t.deactivatingBtn : t.deactivateBtn}
          </button>
        )}
      </div>
    </div>
  );

  const Section = ({ title, color, items }) => {
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
          <h3 style={{ fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: .5 }}>
            {title} <span style={{ color: '#9ca3af', fontWeight: 500 }}>({items.length})</span>
          </h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(u => <UserCard key={u.id} user={u} />)}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t.searchPlaceholder}
            style={{ width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
          />
        </div>
        <select value={filter.product_scope} onChange={e => setFilter(f => ({ ...f, product_scope: e.target.value }))}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
          <option value="">{t.allProducts}</option>
          <option value="GOVERNANCE">Governance</option>
          <option value="SPORTS">Sports</option>
          <option value="BOTH">{t.sectionVukzInternal}</option>
        </select>
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
          <option value="">{t.allStatuses}</option>
          <option value="active">{t.activeFilter}</option>
          <option value="inactive">{t.inactiveFilter}</option>
          <option value="invited">{t.invitedFilter}</option>
        </select>
        <button onClick={load} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>
          <RefreshCw size={14} />
        </button>
        <button onClick={() => setModal('new')} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 8, border: 'none',
          background: G, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
        }}>
          <Plus size={15} /> {t.newUserBtn}
        </button>
      </div>

      {loading ? <Spinner /> : users.length === 0 ? (
        <EmptyState message={t.emptyUsers} />
      ) : (
        <>
          <Section title={t.sectionGovernance} color="#1e40af" items={govUsers} />
          <Section title={t.sectionSports} color="#166534" items={sportsUsers} />
          <Section title={t.sectionVukzInternal} color="#9a3412" items={vukzUsers} />
          <Section title={t.sectionNoProduct} color="#9ca3af" items={otherUsers} />
        </>
      )}

      {modal && (
        <UserModal
          editUser={modal === 'new' ? null : modal}
          roles={roles}
          t={t}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}

// ─── ABA 2: Papéis ───────────────────────────────────────────────────────
function TabPapeis() {
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AZURE.get('/governance/roles')
      .then(({ data }) => setGrouped(data?.data?.grouped || {}))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const scopeOrder = ['GOVERNANCE', 'SPORTS', 'BOTH'];
  const entries = [
    ...scopeOrder.filter(k => grouped[k]).map(k => [k, grouped[k]]),
    ...Object.entries(grouped).filter(([k]) => !scopeOrder.includes(k)),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {entries.map(([scope, roles]) => {
        const c = PRODUCT_COLOR[scope] || PRODUCT_COLOR.GOVERNANCE;
        return (
          <div key={scope}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: c.bg, border: `1px solid ${c.border}`, color: c.text,
              borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700,
              marginBottom: 10,
            }}>
              {PRODUCT_LABEL[scope] || scope}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
              {roles.map(r => (
                <div key={r.role_key} style={{
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                  padding: '12px 14px',
                }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 4 }}>
                    {r.display_name || r.role_key}
                  </div>
                  <code style={{ fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                    {r.role_key}
                  </code>
                  {r.description && (
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
                      {r.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── ABA 3: Permissões ───────────────────────────────────────────────────
function TabPermissoes({ t }) {
  const [entData, setEntData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AZURE.get('/governance/entitlements')
      .then(({ data }) => setEntData(data?.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!entData) return <EmptyState message={t.emptySemData} />;

  // Extrair módulos e ações únicos para a matriz
  const ents = entData.entitlements || [];
  const modules = [...new Set(ents.map(e => e.product_code || 'OUTROS'))];
  const roles   = [...new Set(ents.map(e => e.role_code))].sort();

  const matrix = {};
  ents.forEach(e => {
    (matrix[e.product_code] = matrix[e.product_code] || {})[e.role_code] = e;
  });

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
        {t.permissionsSubtitle}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 700 }}>
                {t.colModule}
              </th>
              {roles.map(r => (
                <th key={r} style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {r.replace(/_/g, '\u200B_')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.map((mod, i) => (
              <tr key={mod} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #f1f5f9' }}>
                  {PRODUCT_LABEL[mod] || mod}
                </td>
                {roles.map(r => {
                  const e = matrix[mod]?.[r];
                  return (
                    <td key={r} style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f1f5f9' }}>
                      {e ? (
                        e.is_add_on
                          ? <span title="Add-on opcional" style={{ color: '#f59e0b', fontSize: 16 }}>+</span>
                          : e.is_required
                            ? <CheckCircle size={14} style={{ color: '#16a34a', margin: '0 auto', display: 'block' }} />
                            : <CheckCircle size={14} style={{ color: '#6b7280', margin: '0 auto', display: 'block', opacity: .5 }} />
                      ) : (
                        <span style={{ color: '#e5e7eb' }}>—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 16, fontSize: 12, color: '#6b7280' }}>
        <span><CheckCircle size={12} style={{ color: '#16a34a', verticalAlign: 'middle' }} /> {t.legendMandatory}</span>
        <span><CheckCircle size={12} style={{ color: '#9ca3af', verticalAlign: 'middle', opacity: .5 }} /> {t.legendIncluded}</span>
        <span style={{ color: '#f59e0b', fontWeight: 700 }}>+</span>
        <span>{t.legendAddOn}</span>
      </div>
    </div>
  );
}

// ─── ABA 4: Planos × Usuários ─────────────────────────────────────────────
function TabPlanos({ t }) {
  const [allEnts, setAllEnts] = useState([]);
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen]       = useState({});

  useEffect(() => {
    AZURE.get('/governance/entitlements')
      .then(({ data }) => {
        const ents = data?.data?.entitlements || [];
        setAllEnts(ents);
        // Extrair planos únicos
        const seen = {};
        ents.forEach(e => {
          if (!seen[e.plan_code]) {
            seen[e.plan_code] = {
              plan_code:    e.plan_code,
              plan_name:    e.plan_name,
              tier:         e.tier,
              price_usd:    e.price_usd,
              product_code: e.product_code,
              product_name: e.product_name,
            };
          }
        });
        setPlans(Object.values(seen));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const byProduct = {};
  plans.forEach(p => {
    (byProduct[p.product_code] = byProduct[p.product_code] || []).push(p);
  });

  const TIER_ORDER = { starter: 0, pro: 1, elite: 2, enterprise: 3 };

  const PlanCard = ({ plan }) => {
    const ents = allEnts.filter(e => e.plan_code === plan.plan_code);
    const required = ents.filter(e => e.is_required);
    const included = ents.filter(e => !e.is_required && !e.is_add_on);
    const addons   = ents.filter(e => e.is_add_on);
    const isOpen = open[plan.plan_code];
    const c = PRODUCT_COLOR[plan.product_code] || PRODUCT_COLOR.GOVERNANCE;

    return (
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
        overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.06)',
      }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${G} 0%, #047857 100%)`, padding: '16px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#a7f3d0', textTransform: 'uppercase', letterSpacing: .5 }}>
                {plan.product_name}
              </p>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: '2px 0' }}>
                {plan.plan_name}
              </h3>
              <span style={{
                background: GOLD, color: '#fff',
                borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700,
              }}>
                {plan.tier.toUpperCase()}
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>
                ${plan.price_usd?.toFixed(0) || '—'}
              </p>
              <p style={{ fontSize: 11, color: '#a7f3d0' }}>{t.perMonth}</p>
            </div>
          </div>
        </div>

        {/* Gestores obrigatórios */}
        {required.length > 0 && (
          <div style={{ padding: '12px 18px', background: '#fefce8', borderBottom: '1px solid #fde68a' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 6 }}>
              {t.mandatoryToActivate}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {required.map(e => (
                <span key={e.role_code} style={{
                  background: '#fef3c7', color: '#92400e',
                  border: '1px solid #fde68a', borderRadius: 6,
                  padding: '3px 8px', fontSize: 12, fontWeight: 700,
                }}>
                  {e.qty_included}× {e.role_name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Detalhes expansíveis */}
        <div style={{ padding: '12px 18px' }}>
          <button
            onClick={() => setOpen(o => ({ ...o, [plan.plan_code]: !o[plan.plan_code] }))}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
              color: G, fontWeight: 600, background: 'none', border: 'none',
              cursor: 'pointer', padding: 0,
            }}
          >
            {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {isOpen ? t.hideDetails : t.showDetails}
          </button>

          {isOpen && (
            <div style={{ marginTop: 12 }}>
              {included.length > 0 && (
                <>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                    {t.includedInPlanLabel}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                    {included.map(e => (
                      <div key={e.role_code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#374151', fontWeight: 500 }}>{e.role_name}</span>
                        <span style={{ color: '#6b7280' }}>
                          {e.qty_included} {t.availableLabel}{e.qty_included !== 1 ? 's' : ''}
                          {e.qty_max > 0 ? ` · ${t.maxLabel} ${e.qty_max}` : e.qty_max === 0 ? ` · ${t.unlimitedLabel}` : ''}
                          {e.price_per_extra > 0 ? ` · +$${e.price_per_extra}${t.extraLabel}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {addons.length > 0 && (
                <>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                    {t.addOnsAvailableLabel}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {addons.map(e => (
                      <div key={e.role_code} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#374151', fontWeight: 500 }}>{e.role_name}</span>
                        <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                          ${e.price_per_extra}{t.perUserLabel}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const productOrder = ['GOVERNANCE', 'SPORTS', 'FULL_SUITE'];

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
        {t.plansSubtitle}
      </p>

      {productOrder.filter(p => byProduct[p]).map(productCode => (
        <div key={productCode} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{
              ...PRODUCT_COLOR[productCode],
              border: `1px solid ${PRODUCT_COLOR[productCode]?.border}`,
              borderRadius: 8, padding: '4px 14px', fontSize: 13, fontWeight: 700,
            }}>
              {PRODUCT_LABEL[productCode]}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {(byProduct[productCode] || [])
              .sort((a, b) => (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9))
              .map(p => <PlanCard key={p.plan_code} plan={p} />)
            }
          </div>
        </div>
      ))}

      {/* Regras comerciais */}
      <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', marginTop: 8 }}>
        <p style={{ fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 8 }}>{t.commercialRulesTitle}</p>
        <ul style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.8, paddingLeft: 16 }}>
          <li>{t.rule1}</li>
          <li>{t.rule2}</li>
          <li>{t.rule3}</li>
          <li>{t.rule4}</li>
          <li>{t.rule5}</li>
        </ul>
      </div>
    </div>
  );
}

// ─── ABA 5: Logs ─────────────────────────────────────────────────────────
function TabLogs({ t }) {
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    user_email: '', module: '', action: '', date_from: '', date_to: '',
  });
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await AZURE.get('/governance/audit-logs', { params });
      setLogs(data?.data?.logs || []);
      setTotal(data?.data?.total || 0);
    } catch (e) {
      console.error('[gov-logs]', e);
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => { load(); }, [load]);

  const ACTION_COLOR = {
    create: { bg: '#dcfce7', text: '#166534' },
    update: { bg: '#dbeafe', text: '#1e40af' },
    delete: { bg: '#fee2e2', text: '#991b1b' },
    view:   { bg: '#f3f4f6', text: '#374151' },
    export: { bg: '#fef9c3', text: '#854d0e' },
  };

  return (
    <>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {[
          { key: 'user_email', placeholder: t.logEmailPlaceholder },
          { key: 'module',     placeholder: t.logModulePlaceholder },
          { key: 'action',     placeholder: t.logActionPlaceholder },
        ].map(f => (
          <input
            key={f.key}
            value={filters[f.key]}
            onChange={e => { setOffset(0); setFilters(p => ({ ...p, [f.key]: e.target.value })); }}
            placeholder={f.placeholder}
            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12, flex: '1 1 160px' }}
          />
        ))}
        <input type="date" value={filters.date_from}
          onChange={e => setFilters(p => ({ ...p, date_from: e.target.value }))}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12 }}
        />
        <input type="date" value={filters.date_to}
          onChange={e => setFilters(p => ({ ...p, date_to: e.target.value }))}
          style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12 }}
        />
        <button onClick={() => { setOffset(0); load(); }}
          style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: G, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
          {t.filterBtn}
        </button>
      </div>

      <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
        {t.recordsFound(total)}
      </p>

      {loading ? <Spinner /> : logs.length === 0 ? (
        <EmptyState message={t.emptyLogs} />
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {logs.map(log => {
              const ac = ACTION_COLOR[log.action] || ACTION_COLOR.view;
              return (
                <div key={log.id} style={{
                  background: '#fff', border: '1px solid #f1f5f9',
                  borderRadius: 8, padding: '10px 14px',
                  display: 'grid', gridTemplateColumns: '110px 1fr auto',
                  alignItems: 'center', gap: 12, fontSize: 12,
                }}>
                  <span style={{
                    ...ac, borderRadius: 6, padding: '3px 8px',
                    fontWeight: 700, textAlign: 'center', fontSize: 11,
                    border: `1px solid ${ac.text}22`
                  }}>
                    {log.action}
                  </span>
                  <div>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{log.user_email}</span>
                    {log.module && <span style={{ color: '#9ca3af' }}> · {log.module}</span>}
                    {log.resource_type && <span style={{ color: '#9ca3af' }}> · {log.resource_type} #{log.resource_id}</span>}
                    {log.institution_id && <span style={{ color: '#9ca3af' }}> · tenant {log.institution_id}</span>}
                  </div>
                  <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {log.created_at ? new Date(log.created_at).toLocaleString(t.dateLocale) : '—'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Paginação */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}
              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', cursor: offset === 0 ? 'not-allowed' : 'pointer', opacity: offset === 0 ? .5 : 1, fontSize: 12 }}>
              {t.prevPage}
            </button>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {offset + 1}–{Math.min(offset + LIMIT, total)} / {total}
            </span>
            <button disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}
              style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', cursor: offset + LIMIT >= total ? 'not-allowed' : 'pointer', opacity: offset + LIMIT >= total ? .5 : 1, fontSize: 12 }}>
              {t.nextPage}
            </button>
          </div>
        </>
      )}
    </>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────
const getTabs = (t) => [
  { id: 'usuarios',    label: t.tabUsers,       icon: Users    },
  { id: 'papeis',      label: t.tabRoles,       icon: Shield   },
  { id: 'permissoes',  label: t.tabPermissions, icon: Grid     },
  { id: 'planos',      label: t.tabPlans,       icon: CreditCard },
  { id: 'logs',        label: t.tabLogs,        icon: FileText },
];

export default function GovernanceUsersPage() {
  const [activeTab, setActiveTab] = useState('usuarios');
  const [roles, setRoles]         = useState([]);
  const { lang } = useGovernanceLang();
  const t = getT(lang);
  const tabs = getTabs(t);

  // Carregar roles uma vez para o modal
  useEffect(() => {
    AZURE.get('/governance/roles')
      .then(({ data }) => setRoles(data?.data?.roles || []))
      .catch(console.error);
  }, []);

  return (
    <div style={{ backgroundColor: BG, minHeight: '100vh', padding: '24px 24px 40px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: G, margin: 0 }}>
          {t.pageTitle}
        </h1>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
          {t.pageSubtitle}
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        background: '#fff', borderRadius: 10,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 4px rgba(0,0,0,.04)',
        overflow: 'hidden',
      }}>
        {/* Tab bar */}
        <div style={{
          display: 'flex', overflowX: 'auto',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 16px',
        }}>
          {tabs.map(tab => (
            <TabButton
              key={tab.id}
              label={tab.label}
              icon={tab.icon}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: 20 }}>
          {activeTab === 'usuarios'   && <TabUsuarios   roles={roles} t={t} />}
          {activeTab === 'papeis'     && <TabPapeis />}
          {activeTab === 'permissoes' && <TabPermissoes t={t} />}
          {activeTab === 'planos'     && <TabPlanos t={t} />}
          {activeTab === 'logs'       && <TabLogs t={t} />}
        </div>
      </div>
    </div>
  );
}
